import { loadStripe, Stripe } from '@stripe/stripe-js';

// Stripe publishable key - LIVE PRODUCTION KEY
// Get your live key from: https://dashboard.stripe.com/apikeys
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51RZFEXCFBM4f8BDak3g0fAFM88NpUFxoTqBWVn6qjZgspQUezuYAX9jmCqhlNJfpNucqurHwGT5Ni1vcp8AP73Eb00o2W90MrS';

let stripePromise: Promise<Stripe | null> | null = null;

// Check if we're in a snapshot environment (react-snap)
const isSnapshot = typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

export const getStripe = () => {
  // Don't load Stripe during snapshot
  if (isSnapshot) {
    return Promise.resolve(null);
  }
  
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

// Flexible pricing configuration
export interface PricingConfig {
  monthlySubscription: {
    defaultPricePerTable: number; // $10 default
    negotiatedPricePerTable?: number; // Custom pricing
  };
  nfcHardware: {
    defaultPricePerNFC: number; // $5 default
    negotiatedPricePerNFC?: number; // Custom pricing
  };
  tabletHardware?: {
    defaultPricePerTablet: number; // $495 default
    negotiatedPricePerTablet?: number; // Custom pricing
  };
  setupFee?: number; // Optional setup fee
}

export interface RestaurantSignupData {
  restaurant: {
    name: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    phone: string;
    email: string;
    website?: string;
    description?: string;
  };
  manager: {
    displayName: string;
    email: string;
    phoneNumber: string;
  };
  tableCount: number;
  nfcCount: number;
  pricing: PricingConfig;
}

export interface PaymentCalculation {
  tableCount: number;
  nfcCount: number;
  tabletCount: number;
  pricePerTable: number;
  pricePerNFC: number;
  pricePerTablet: number;
  setupFee: number;
  
  // Calculations
  monthlySubscriptionCost: number;
  nfcHardwareCost: number;
  tabletHardwareCost: number;
  totalInitialPayment: number; // First month + NFC hardware + Tablet hardware + setup fee
  monthlyRecurringPayment: number; // Just subscription
}

// Default pricing configuration
export const DEFAULT_PRICING: PricingConfig = {
  monthlySubscription: {
    defaultPricePerTable: 10.00, // $10 per table per month
  },
  nfcHardware: {
    defaultPricePerNFC: 5.00, // $5 per NFC tag
  },
  tabletHardware: {
    defaultPricePerTablet: 495.00, // $495 per tablet
  },
  setupFee: 0, // No setup fee by default
};

// Calculate pricing with flexibility for negotiation
export const calculatePricing = (
  tableCount: number,
  nfcCount: number,
  tabletCount: number = 0,
  customPricing?: Partial<PricingConfig>
): PaymentCalculation => {
  // Standard flat-rate pricing
  const FLAT_RATE_INITIAL = 379.00;  // Pay today amount
  const FLAT_RATE_MONTHLY = 379.00;  // Monthly recurring
  
  // Keep the original calculation logic for reference/future use
  const pricing = {
    ...DEFAULT_PRICING,
    ...customPricing,
  };

  const pricePerTable = pricing.monthlySubscription.negotiatedPricePerTable || 
                       pricing.monthlySubscription.defaultPricePerTable;
  
  const pricePerNFC = pricing.nfcHardware.negotiatedPricePerNFC || 
                     pricing.nfcHardware.defaultPricePerNFC;
  
  const pricePerTablet = pricing.tabletHardware?.negotiatedPricePerTablet || 
                        pricing.tabletHardware?.defaultPricePerTablet || 495.00;
  
  const setupFee = pricing.setupFee || 0;

  // OVERRIDE: Return flat rate pricing
  return {
    tableCount,
    nfcCount,
    tabletCount,
    pricePerTable,
    pricePerNFC,
    pricePerTablet,
    setupFee,
    monthlySubscriptionCost: FLAT_RATE_MONTHLY,
    nfcHardwareCost: 0,  // Included in flat rate
    tabletHardwareCost: 0,  // Included in flat rate
    totalInitialPayment: FLAT_RATE_INITIAL,
    monthlyRecurringPayment: FLAT_RATE_MONTHLY,
  };
};

// Create payment intent for initial payment
export const createPaymentIntent = async (
  paymentCalculation: PaymentCalculation,
  customerData: RestaurantSignupData
) => {
  try {
    const response = await fetch('/api/create-payment-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: Math.round(paymentCalculation.totalInitialPayment * 100), // Convert to cents
        currency: 'usd',
        customerData,
        paymentCalculation,
        paymentType: 'initial_setup',
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create payment intent');
    }

    const { clientSecret, paymentIntentId } = await response.json();
    return { clientSecret, paymentIntentId };
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw error;
  }
};

// Create subscription for recurring payments
export const createSubscription = async (
  customerId: string,
  paymentCalculation: PaymentCalculation
) => {
  try {
    const response = await fetch('/api/create-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customerId,
        amount: Math.round(paymentCalculation.monthlyRecurringPayment * 100), // Convert to cents
        tableCount: paymentCalculation.tableCount,
        pricePerTable: paymentCalculation.pricePerTable,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create subscription');
    }

    const subscription = await response.json();
    return subscription;
  } catch (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }
};

// Pricing tiers for marketing (can be overridden with custom pricing)
export const PRICING_TIERS = {
  starter: {
    name: 'Starter',
    description: 'Perfect for small restaurants',
    monthlySubscription: {
      defaultPricePerTable: 10.00,
    },
    nfcHardware: {
      defaultPricePerNFC: 5.00,
    },
    maxTables: 10,
  },
  professional: {
    name: 'Professional',
    description: 'Great for growing restaurants',
    monthlySubscription: {
      defaultPricePerTable: 9.00, // Volume discount
    },
    nfcHardware: {
      defaultPricePerNFC: 4.50, // Volume discount
    },
    maxTables: 25,
  },
  enterprise: {
    name: 'Enterprise',
    description: 'For large restaurant chains',
    monthlySubscription: {
      defaultPricePerTable: 8.00, // Larger volume discount
    },
    nfcHardware: {
      defaultPricePerNFC: 4.00, // Larger volume discount
    },
    maxTables: Infinity,
  },
};

const stripeService = {
  getStripe,
  calculatePricing,
  createPaymentIntent,
  createSubscription,
  DEFAULT_PRICING,
  PRICING_TIERS,
};

export default stripeService;
